<template>
  <nav class="absolute top-4 left-4 z-40 flex w-fit shrink-0">
    <section class="flex flex-1 items-start justify-between gap-4 pb-1">
      <section class="flex items-center gap-2 rounded-lg bg-white p-2 shadow-md">
        <!-- Back Button -->
        <button
          class="flex h-9 w-9 items-center justify-center rounded-md transition-colors hover:bg-gray-100"
          @click="goBack"
        >
          <ArrowLeftIcon class="text-bluedark-700 h-5 w-5" />
        </button>

        <template v-if="boards && boards.length > 0">
          <!-- board selector dropdown -->
          <ScenariosDropdown :board-id="boardId" data-test="main-org-plan-selector" />
        </template>

        <!-- Org, Directory, Forecast, Hub views -->
        <MainToolbarViews v-if="menuEntries?.length" :views="menuEntries" />
      </section>
    </section>
  </nav>
</template>

<script setup>
import ArrowLeftIcon from '@/assets/untitled-ui/arrow-left.svg'
import useBoard from '@/hooks/use-board'
import { useKeyboard } from '@/hooks/use-keyboard.js'
import useNavigation from '@/hooks/use-navigation'
import { FEATURES } from '@/lib/Features'
import { useMagicKeys } from '@vueuse/core'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import MainToolbarViews from './MainToolbarViews.vue'
import ScenariosDropdown from '@/components/ScenariosDropdown.vue'

const { navigationConfig } = useNavigation()
const router = useRouter()
const { boardId } = useBoard()
const { getters } = useStore()
const { isUsingInput } = useKeyboard()

const boards = computed(() => getters.boards)

const availableFeatures = [
  FEATURES.OrgChart,
  FEATURES.Directory,
  FEATURES.Forecast,
  FEATURES.Hub,
  FEATURES.Productivity
]

const uiElementConfig = {
  [FEATURES.OrgChart]: {
    dataTest: 'main-org-view-button'
  },
  [FEATURES.Directory]: {
    dataTest: 'main-list-view-button'
  },
  [FEATURES.Forecast]: {
    dataTest: 'main-forecast-view-button'
  },
  [FEATURES.Hub]: {
    dataTest: 'main-hub-view-button'
  }
}

const menuEntries = computed(() =>
  availableFeatures
    .map((feature) => ({
      ...navigationConfig.value[feature],
      ...uiElementConfig[feature]
    }))
    .filter((feature) => feature.show)
)

const goBack = () => {
  // Navigate back in browser history
  router.back()
}

useMagicKeys({
  passive: false,
  onEventFired(e) {
    if (isUsingInput.value || e.type !== 'keydown') return

    const keyIndex = parseInt(e.key)
    const configEntry = menuEntries.value.find((entry) => entry.keyboardShortcut === keyIndex)

    if (!configEntry) return

    router.push({ path: configEntry.url })
  }
})
</script>
